import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  FiArrowLeft,
  FiShare2,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiChevronDown
} from 'react-icons/fi';
import { MdTheaters } from 'react-icons/md';

const ScriptBreakdown = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pageRef = useRef(null);

  // Get script data from navigation state
  const {
    scriptContent = '',
    conversationId = null,
    projectId = null,
    sceneBreakdown: sceneBreakdownData = null
  } = location.state || {};

  // State management
  const [scenes, setScenes] = useState([]);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState('classic');
  const [isAnimating, setIsAnimating] = useState(false);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);

  // Template configurations
  const templates = {
    classic: {
      name: 'Classic Screenplay',
      bgColor: '#ffffff',
      textColor: '#000000',
      fontFamily: "'Courier Prime', 'Courier New', Courier, monospace",
      pageColor: '#ffffff',
      pageShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
    },
    night: {
      name: 'Night Mode',
      bgColor: '#0f172a',
      textColor: '#e2e8f0',
      fontFamily: "'Courier Prime', 'Courier New', Courier, monospace",
      pageColor: '#1e293b',
      pageShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
    },
    vintage: {
      name: 'Vintage Paper',
      bgColor: '#f5f5dc',
      textColor: '#2d2d2d',
      fontFamily: "'Courier Prime', 'Courier New', Courier, monospace",
      pageColor: '#faf8f3',
      pageShadow: '0 4px 20px rgba(139, 69, 19, 0.2)'
    },
    modern: {
      name: 'Modern Clean',
      bgColor: '#f8f9fa',
      textColor: '#212529',
      fontFamily: "'Courier Prime', 'Courier New', Courier, monospace",
      pageColor: '#ffffff',
      pageShadow: '0 2px 15px rgba(0, 0, 0, 0.08)'
    }
  };

  // Parse script into scenes on mount
  useEffect(() => {
    if (scriptContent) {
      parseScriptIntoScenes(scriptContent);
    }
  }, [scriptContent]);

  // ✅ Keyboard navigation (FUNCTIONALITY REMAINS - NO DISPLAY)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        goToPreviousScene();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        goToNextScene();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSceneIndex, scenes.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showTemplateDropdown && !e.target.closest('.sb-template-dropdown')) {
        setShowTemplateDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTemplateDropdown]);

  // Parse script into individual scenes
  const parseScriptIntoScenes = (scriptText) => {
    // Remove HTML tags if present
    const cleanText = scriptText.replace(/<[^>]*>/g, '\n').replace(/\*\*/g, '');

    // Multi-language scene markers
    // Matches: Scene, सीन, दृश्य, INT., EXT., ACT, एक्ट, etc.
    const sceneMarkers = [
      /(?:^|\n)\s*(?:SCENE|सीन|दृश्य|Scene)\s*(\d+)/gi,
      /(?:^|\n)\s*(?:INT\.|EXT\.)\s*[^\n]*/gi,
      /(?:^|\n)\s*(?:ACT|एक्ट|अधिनियम)\s*\d+/gi
    ];

    let parts = [cleanText];
    let matchedScenes = [];

    // Try each pattern
    for (const pattern of sceneMarkers) {
      const matches = [...cleanText.matchAll(pattern)];
      if (matches.length > 0) {
        matchedScenes = matches;
        break;
      }
    }

    const parsedScenes = [];

    if (matchedScenes.length > 0) {
      // Split by detected scene markers
      let lastIndex = 0;
      matchedScenes.forEach((match, i) => {
        const startIndex = match.index;
        const nextMatch = matchedScenes[i + 1];
        const endIndex = nextMatch ? nextMatch.index : cleanText.length;

        const sceneText = cleanText.substring(startIndex, endIndex).trim();
        const heading = match[0].trim();
        const content = sceneText.substring(heading.length).trim();

        if (sceneText) {
          const sceneInfo = extractSceneInfo(heading, content, i + 1);
          parsedScenes.push({
            number: i + 1,
            heading: heading,
            content: content,
            ...sceneInfo
          });
        }
      });
    } else {
      // No clear scene markers - split by paragraphs or INT/EXT
      const lines = cleanText.split('\n').filter(l => l.trim());
      let currentScene = { heading: 'Scene 1', content: [], number: 1 };
      let sceneNum = 1;

      lines.forEach(line => {
        const trimmed = line.trim();
        // Check if it's a new scene heading (INT/EXT or contains scene keywords)
        if (/^(INT\.|EXT\.|सीन|Scene|दृश्य)/i.test(trimmed)) {
          if (currentScene.content.length > 0) {
            parsedScenes.push({
              ...currentScene,
              content: currentScene.content.join('\n'),
              ...extractSceneInfo(currentScene.heading, currentScene.content.join('\n'), sceneNum)
            });
            sceneNum++;
          }
          currentScene = { heading: trimmed, content: [], number: sceneNum };
        } else {
          currentScene.content.push(trimmed);
        }
      });

      // Add last scene
      if (currentScene.content.length > 0) {
        parsedScenes.push({
          ...currentScene,
          content: currentScene.content.join('\n'),
          ...extractSceneInfo(currentScene.heading, currentScene.content.join('\n'), sceneNum)
        });
      }
    }

    // If no scenes detected, treat entire script as one scene
    if (parsedScenes.length === 0) {
      parsedScenes.push({
        number: 1,
        heading: 'Scene 1',
        content: cleanText.trim(),
        location: 'Unknown',
        timeOfDay: 'DAY',
        setting: 'INT'
      });
    }

    setScenes(parsedScenes);
  };

  // Extract scene information
  const extractSceneInfo = (heading, content, sceneNum) => {
    const info = {
      location: 'Unknown Location',
      timeOfDay: 'DAY',
      setting: 'INT'
    };

    const headingUpper = heading.toUpperCase();

    // Extract setting (INT/EXT)
    if (headingUpper.includes('INT')) {
      info.setting = 'INT';
    } else if (headingUpper.includes('EXT')) {
      info.setting = 'EXT';
    }

    // Extract time of day (multi-language support)
    if (headingUpper.includes('NIGHT') || heading.includes('रात') || heading.includes('रात्रि')) {
      info.timeOfDay = 'NIGHT';
    } else if (headingUpper.includes('DAY') || heading.includes('दिन')) {
      info.timeOfDay = 'DAY';
    } else if (headingUpper.includes('DAWN') || heading.includes('सुबह') || heading.includes('प्रातः')) {
      info.timeOfDay = 'DAWN';
    } else if (headingUpper.includes('DUSK') || heading.includes('शाम') || heading.includes('संध्या')) {
      info.timeOfDay = 'DUSK';
    }

    // Extract location
    const locationMatch = heading.match(/[-–—]\s*([^-–—\n]+)/);
    if (locationMatch) {
      info.location = locationMatch[1].trim();
    } else {
      // Try to get from first line of content
      const firstLine = content.split('\n')[0];
      if (firstLine && firstLine.length < 50) {
        info.location = firstLine.substring(0, 40);
      }
    }

    return info;
  };

  // Navigation functions
  const goToNextScene = () => {
    if (currentSceneIndex < scenes.length - 1 && !isAnimating) {
      setIsAnimating(true);
      setCurrentSceneIndex(currentSceneIndex + 1);
      setTimeout(() => setIsAnimating(false), 600);
    }
  };

  const goToPreviousScene = () => {
    if (currentSceneIndex > 0 && !isAnimating) {
      setIsAnimating(true);
      setCurrentSceneIndex(currentSceneIndex - 1);
      setTimeout(() => setIsAnimating(false), 600);
    }
  };

  const goToScene = (index) => {
    if (index !== currentSceneIndex && !isAnimating) {
      setIsAnimating(true);
      setCurrentSceneIndex(index);
      setTimeout(() => setIsAnimating(false), 600);
    }
  };

  // Back to chat navigation
  const handleBackToChat = () => {
    if (projectId) {
      navigate(`/input/${projectId}`, {
        state: {
          activePage: 2,
          conversationId: conversationId,
          shouldLoadConversation: true
        }
      });
    } else {
      navigate(-1);
    }
  };

  // Format script content for display with proper screenplay formatting
  const formatSceneContent = (content) => {
    const lines = content.split('\n').filter(line => line.trim());
    const formatted = [];

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) return;

      // Character name (all caps, not too long, typically followed by dialogue)
      if (trimmed === trimmed.toUpperCase() && 
          trimmed.length > 0 && 
          trimmed.length < 30 && 
          !/^(INT\.|EXT\.|FADE|CUT TO)/.test(trimmed) &&
          !trimmed.match(/^\d/) &&
          index < lines.length - 1) {
        formatted.push(
          <div key={`char-${index}`} className="sb-character-name">
            {trimmed}
          </div>
        );
      }
      // Parenthetical (in parentheses)
      else if (trimmed.startsWith('(') && trimmed.endsWith(')')) {
        formatted.push(
          <div key={`paren-${index}`} className="sb-parenthetical">
            {trimmed}
          </div>
        );
      }
      // Scene heading (INT/EXT or all caps with location)
      else if (/^(INT\.|EXT\.)/.test(trimmed) || 
               (trimmed === trimmed.toUpperCase() && trimmed.length > 30)) {
        formatted.push(
          <div key={`heading-${index}`} className="sb-scene-heading-inline">
            {trimmed}
          </div>
        );
      }
      // Transition (FADE IN, CUT TO, etc.)
      else if (/^(FADE|CUT TO|DISSOLVE|SMASH CUT)/.test(trimmed)) {
        formatted.push(
          <div key={`trans-${index}`} className="sb-transition">
            {trimmed}
          </div>
        );
      }
      // Dialogue or action
      else {
        // Check if previous element was character name (then this is dialogue)
        const prevElement = formatted[formatted.length - 1];
        const isDialogue = prevElement && prevElement.props.className === 'sb-character-name';
        
        formatted.push(
          <div 
            key={`action-${index}`} 
            className={isDialogue ? 'sb-dialogue' : 'sb-action'}
          >
            {trimmed}
          </div>
        );
      }
    });

    return formatted;
  };

  const currentTemplate = templates[selectedTemplate];
  const currentScene = scenes[currentSceneIndex];

  if (!scriptContent) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px',
        background: '#f8f9fa'
      }}>
        <FiFileText size={64} color="#ccc" />
        <p style={{ fontSize: '20px', color: '#666' }}>
          No script content available. Please generate a script first.
        </p>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '12px 24px',
            background: '#14b8a6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Import Courier Prime font */
        @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap');

        /* Main Container */
        .sb-container {
          display: flex;
          height: 100vh;
          width: 100%;
          overflow: hidden;
          background: ${currentTemplate.bgColor};
          transition: background-color 0.3s ease;
          font-family: ${currentTemplate.fontFamily};
        }

        /* Scene Selector Sidebar */
        .sb-sidebar {
          width: 280px;
          background: #2c3e50;
          color: white;
          display: flex;
          flex-direction: column;
          box-shadow: 2px 0 10px rgba(0, 0, 0, 0.3);
          overflow-y: auto;
          flex-shrink: 0;
        }

        .sb-sidebar-header {
          padding: 20px;
          background: #1a252f;
          border-bottom: 2px solid #34495e;
        }

        .sb-sidebar-title {
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 8px 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .sb-sidebar-subtitle {
          font-size: 13px;
          opacity: 0.7;
          margin: 0;
        }

        .sb-scene-list {
          flex: 1;
          overflow-y: auto;
          padding: 10px;
        }

        .sb-scene-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          margin-bottom: 8px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          border-left: 3px solid transparent;
        }

        .sb-scene-item:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: translateX(4px);
        }

        /* ✅ TEAL THEME: Changed from blue to teal */
        .sb-scene-item.sb-active {
          background: #14b8a6;
          border-left-color: #10b981;
          box-shadow: 0 2px 8px rgba(20, 184, 166, 0.4);
        }

        .sb-scene-icon {
          font-size: 24px;
          opacity: 0.8;
          flex-shrink: 0;
        }

        .sb-scene-info {
          flex: 1;
          min-width: 0;
        }

        .sb-scene-number {
          font-size: 16px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .sb-scene-meta {
          font-size: 11px;
          opacity: 0.7;
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .sb-scene-meta-item {
          background: rgba(255, 255, 255, 0.1);
          padding: 2px 6px;
          border-radius: 4px;
        }

        /* Main Content Area */
        .sb-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          min-width: 0;
        }

        /* Top Header */
        .sb-header {
          background: #1a252f;
          color: white;
          padding: 16px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          z-index: 10;
          flex-shrink: 0;
        }

        .sb-header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        /* ✅ TEAL THEME: Changed from blue to teal */
        .sb-back-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: #14b8a6;
          border: none;
          border-radius: 6px;
          color: white;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .sb-back-btn:hover {
          background: #0d9488;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        }

        .sb-header-title {
          font-size: 20px;
          font-weight: 700;
          margin: 0;
        }

        .sb-header-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        /* Template Dropdown */
        .sb-template-dropdown {
          position: relative;
        }

        .sb-template-btn {
          padding: 8px 16px;
          background: #34495e;
          border: 1px solid #4a5f7f;
          border-radius: 6px;
          color: white;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
          font-family: inherit;
        }

        .sb-template-btn:hover {
          background: #4a5f7f;
        }

        .sb-template-menu {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
          min-width: 200px;
          overflow: hidden;
          z-index: 100;
        }

        .sb-template-option {
          padding: 12px 16px;
          cursor: pointer;
          transition: background 0.2s;
          color: #2c3e50;
          font-size: 14px;
        }

        .sb-template-option:hover {
          background: #ecf0f1;
        }

        /* ✅ TEAL THEME: Changed from blue to teal */
        .sb-template-option.sb-selected {
          background: #14b8a6;
          color: white;
          font-weight: 600;
        }

        .sb-action-btn {
          padding: 8px 16px;
          background: transparent;
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 6px;
          color: white;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
          position: relative;
          font-family: inherit;
        }

        .sb-action-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .sb-action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .sb-coming-soon {
          position: absolute;
          top: -8px;
          right: -8px;
          background: #e74c3c;
          color: white;
          font-size: 9px;
          padding: 2px 6px;
          border-radius: 10px;
          font-weight: 700;
        }

        /* ✅ Page Area with TOP SPACING */
        .sb-page-area {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 60px 40px 40px 40px;  /* Added 60px top padding */
          overflow-y: auto;
          perspective: 2000px;
          position: relative;
        }

        /* Navigation Arrows */
        .sb-nav-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          cursor: pointer;
          transition: all 0.3s;
          z-index: 10;
        }

        .sb-nav-arrow:hover:not(:disabled) {
          background: rgba(0, 0, 0, 0.8);
          transform: translateY(-50%) scale(1.1);
        }

        .sb-nav-arrow:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .sb-nav-left {
          left: 20px;
        }

        .sb-nav-right {
          right: 20px;
        }

        /* Script Page */
        .sb-script-page {
          width: 8.5in;
          min-height: 11in;
          background: ${currentTemplate.pageColor};
          box-shadow: ${currentTemplate.pageShadow};
          padding: 1in 1in 1in 1.5in;
          font-family: ${currentTemplate.fontFamily};
          font-size: 12pt;
          line-height: 2;
          color: ${currentTemplate.textColor};
          position: relative;
          transition: all 0.3s ease;
          animation: ${isAnimating ? 'sb-page-flip 0.6s ease-in-out' : 'none'};
        }

        @keyframes sb-page-flip {
          0% {
            transform: perspective(1000px) rotateY(-20deg) translateX(-50px);
            opacity: 0;
          }
          100% {
            transform: perspective(1000px) rotateY(0deg) translateX(0);
            opacity: 1;
          }
        }

        /* Page Content */
        .sb-page-number {
          position: absolute;
          top: 0.5in;
          right: 1in;
          font-size: 11pt;
          opacity: 0.6;
        }

        .sb-scene-heading-main {
          font-weight: 700;
          text-transform: uppercase;
          margin-bottom: 24px;
          padding-bottom: 12px;
          border-bottom: 2px solid ${currentTemplate.textColor};
        }

        .sb-scene-title {
          font-size: 14pt;
          margin-bottom: 8px;
          letter-spacing: 0.5px;
        }

        .sb-scene-details {
          font-size: 10pt;
          opacity: 0.7;
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        /* Screenplay Elements */
        .sb-scene-heading-inline {
          font-weight: 700;
          text-transform: uppercase;
          margin: 24px 0 12px 0;
          letter-spacing: 0.5px;
        }

        .sb-character-name {
          text-transform: uppercase;
          margin: 24px 0 0 0;
          text-align: center;
          font-weight: 700;
          letter-spacing: 0.5px;
        }

        .sb-parenthetical {
          margin: 4px 0;
          text-align: center;
          font-style: italic;
          padding-left: 1.5in;
          padding-right: 1.5in;
        }

        .sb-dialogue {
          margin: 4px 0 12px 0;
          padding-left: 1in;
          padding-right: 1.5in;
          text-align: left;
        }

        .sb-action {
          margin: 12px 0;
          text-align: left;
        }

        .sb-transition {
          text-align: right;
          font-weight: 700;
          text-transform: uppercase;
          margin: 24px 0;
        }

        .sb-scene-progress {
          position: absolute;
          bottom: 0.5in;
          left: 50%;
          transform: translateX(-50%);
          font-size: 10pt;
          opacity: 0.5;
        }

        /* ✅ REMOVED: Keyboard Hint Display - Functionality still works! */

        /* Responsive */
        @media (max-width: 1400px) {
          .sb-script-page {
            width: 100%;
            max-width: 8.5in;
            min-height: auto;
          }
        }

        @media (max-width: 1200px) {
          .sb-sidebar {
            width: 240px;
          }

          .sb-script-page {
            padding: 0.75in;
          }
        }

        @media (max-width: 768px) {
          .sb-sidebar {
            position: fixed;
            left: -280px;
            height: 100%;
            z-index: 100;
            transition: left 0.3s;
          }

          .sb-sidebar.sb-sidebar-open {
            left: 0;
          }

          .sb-page-area {
            padding: 40px 20px 20px 20px;
          }

          .sb-header-title {
            font-size: 16px;
          }

          .sb-nav-arrow {
            width: 50px;
            height: 50px;
            font-size: 20px;
          }
        }

        /* Scrollbar Styles */
        .sb-sidebar::-webkit-scrollbar,
        .sb-page-area::-webkit-scrollbar,
        .sb-scene-list::-webkit-scrollbar {
          width: 8px;
        }

        .sb-sidebar::-webkit-scrollbar-track {
          background: #1a252f;
        }

        .sb-sidebar::-webkit-scrollbar-thumb {
          background: #34495e;
          border-radius: 4px;
        }

        .sb-sidebar::-webkit-scrollbar-thumb:hover {
          background: #4a5f7f;
        }

        .sb-page-area::-webkit-scrollbar-track {
          background: ${currentTemplate.bgColor};
        }

        .sb-page-area::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 4px;
        }
      `}</style>

      <div className="sb-container">
        {/* Scene Selector Sidebar */}
        <div className="sb-sidebar">
          <div className="sb-sidebar-header">
            <h2 className="sb-sidebar-title">
              <FiFileText />
              Scene Navigator
            </h2>
            <p className="sb-sidebar-subtitle">
              {scenes.length} Scene{scenes.length !== 1 ? 's' : ''} Total
            </p>
          </div>

          <div className="sb-scene-list">
            {scenes.map((scene, index) => (
              <div
                key={index}
                className={`sb-scene-item ${index === currentSceneIndex ? 'sb-active' : ''}`}
                onClick={() => goToScene(index)}
              >
                <MdTheaters className="sb-scene-icon" />
                <div className="sb-scene-info">
                  <div className="sb-scene-number">
                    Scene {scene.number}
                  </div>
                  <div className="sb-scene-meta">
                    <span className="sb-scene-meta-item">{scene.setting}</span>
                    <span className="sb-scene-meta-item">{scene.timeOfDay}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="sb-main">
          {/* Top Header */}
          <div className="sb-header">
            <div className="sb-header-left">
              <button
                className="sb-back-btn"
                onClick={handleBackToChat}
              >
                <FiArrowLeft />
                Back to Script Generator
              </button>
              <h1 className="sb-header-title">
                Script Breakdown
              </h1>
            </div>

            <div className="sb-header-right">
              {/* Template Selector */}
              <div className="sb-template-dropdown">
                <button
                  className="sb-template-btn"
                  onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                >
                  Templates
                  <FiChevronDown />
                </button>
                {showTemplateDropdown && (
                  <div className="sb-template-menu">
                    {Object.keys(templates).map((key) => (
                      <div
                        key={key}
                        className={`sb-template-option ${selectedTemplate === key ? 'sb-selected' : ''}`}
                        onClick={() => {
                          setSelectedTemplate(key);
                          setShowTemplateDropdown(false);
                        }}
                      >
                        {templates[key].name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Visualize Scene */}
              <button className="sb-action-btn" disabled>
                <FiEye />
                Visualize Scene
                <span className="sb-coming-soon">SOON</span>
              </button>

              {/* Share */}
              <button className="sb-action-btn" disabled>
                <FiShare2 />
                Share
                <span className="sb-coming-soon">SOON</span>
              </button>
            </div>
          </div>

          {/* Page Area */}
          <div className="sb-page-area">
            {/* Previous Arrow */}
            <button
              className="sb-nav-arrow sb-nav-left"
              onClick={goToPreviousScene}
              disabled={currentSceneIndex === 0}
              title="Previous Scene (←)"
            >
              <FiChevronLeft />
            </button>

            {/* Script Page */}
            {currentScene && (
              <div ref={pageRef} className="sb-script-page">
                {/* Page Number */}
                <div className="sb-page-number">
                  {currentSceneIndex + 1}
                </div>

                {/* Scene Heading */}
                <div className="sb-scene-heading-main">
                  <div className="sb-scene-title">
                    {currentScene.heading}
                  </div>
                  <div className="sb-scene-details">
                    <span>Scene {currentScene.number}</span>
                    <span>{currentScene.setting}</span>
                    <span>{currentScene.timeOfDay}</span>
                    <span>{currentScene.location}</span>
                  </div>
                </div>

                {/* Scene Content */}
                <div className="sb-scene-content">
                  {formatSceneContent(currentScene.content)}
                </div>

                {/* Scene Progress */}
                <div className="sb-scene-progress">
                  Scene {currentSceneIndex + 1} of {scenes.length}
                </div>
              </div>
            )}

            {/* Next Arrow */}
            <button
              className="sb-nav-arrow sb-nav-right"
              onClick={goToNextScene}
              disabled={currentSceneIndex === scenes.length - 1}
              title="Next Scene (→)"
            >
              <FiChevronRight />
            </button>

            {/* ✅ KEYBOARD HINT REMOVED - Keyboard navigation still works via useEffect! */}
          </div>
        </div>
      </div>
    </>
  );
};

ScriptBreakdown.propTypes = {
  scriptContent: PropTypes.string,
  conversationId: PropTypes.number,
  projectId: PropTypes.string,
  sceneBreakdown: PropTypes.object
};

export default ScriptBreakdown;
